/**
 * UI-3a Task 6 — CampaignDrawer.
 *
 * The wide create/edit drawer for a campaign. Wraps the three campaign tabs
 * (General, Contacts, Config) in a PrimeReact `TabView`, hosted inside the
 * shared `ResourceDrawer` (`width="wide"`).
 *
 * Save ownership
 * --------------
 * The given props interface — `{ open, campaign, onClose, onSaved }` — has no
 * `onSave`, and `onSaved` is the post-save callback. So the drawer itself
 * performs the campaign create/update API call, then calls `onSaved()` (the
 * page wires that to refetch + close):
 *
 *   - create: POST  /campaigns
 *   - update: PUT   /campaigns/:id   (campaign update is PUT — spec §0.4 /
 *                                     apps/api/src/routes/campaigns/update.ts)
 *
 * The General tab owns its own `<ZodForm>`; the drawer drives that form's
 * validated submit through `formRef`. ResourceDrawer's Save button calls
 * `formRef.current.requestSubmit()`, which triggers `CampaignGeneralTab`'s
 * `onSubmit` — `handleGeneralSubmit` here — with the Zod-validated values. That
 * handler assembles `{ ...generalValues, config }` and performs the API call.
 *
 * The Config object and its validity flag are drawer state. Per the Config
 * tab's contract it does NOT report validity at mount, so `configValid`
 * defaults to `true` and only flips on a reported `false`. The Save button is
 * disabled while `configValid === false`.
 *
 * Remount per campaign
 * --------------------
 * `CampaignGeneralTab` and `CampaignConfigTab` both mount-initialize from their
 * props and do not re-sync afterwards. The inner content is therefore given a
 * `key` of `campaign?.id ?? 'new'` so opening a different campaign — or
 * switching between create and edit — fully remounts those children.
 */
import { useRef, useState, type JSX } from 'react'
import { TabView, TabPanel } from 'primereact/tabview'
import ResourceDrawer from '../ResourceDrawer.js'
import { api } from '../../../api/client.js'
import { useToast } from '../../system/ToastProvider.js'
import type { Campaign } from '../../../store/dialerStore.js'
import {
  CampaignGeneralTab,
  type CampaignGeneralValues,
} from './CampaignGeneralTab.js'
import { CampaignContactsTab } from './CampaignContactsTab.js'
import { CampaignConfigTab } from './CampaignConfigTab.js'
import { WebContactManagementTab } from './WebContactManagementTab.js'

// ---------------------------------------------------------------------------
// Public interface
// ---------------------------------------------------------------------------

export interface CampaignDrawerProps {
  open: boolean
  /** The campaign being edited; `null` = create mode. */
  campaign: Campaign | null
  onClose: () => void
  /** Called after a successful save so the parent can refetch + close. */
  onSaved: () => void
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CampaignDrawer({
  open,
  campaign,
  onClose,
  onSaved,
}: CampaignDrawerProps): JSX.Element {
  const toast = useToast()
  const formRef = useRef<HTMLFormElement>(null)

  // The Config object (drawer-owned). Seeded from the campaign on each mount;
  // the `contentKey` below remounts this component's children per campaign.
  const [config, setConfig] = useState<Record<string, unknown>>(
    campaign?.config ?? {},
  )
  // Per the Config tab's contract: it does not report validity at mount, so
  // assume valid and only flip on a reported `false`.
  const [configValid, setConfigValid] = useState(true)
  const [saving, setSaving] = useState(false)

  const isEdit = campaign !== null
  /** Remount key — changes when a different campaign (or create mode) opens. */
  const contentKey = campaign?.id ?? 'new'

  // --- General tab defaults ------------------------------------------------

  const generalDefaults = campaign
    ? {
        name: campaign.name,
        mode: campaign.mode,
        maxCps: campaign.maxCps,
        maxAttemptsPerDay: campaign.maxAttemptsPerDay,
        callingHoursStart: campaign.callingHoursStart,
        callingHoursEnd: campaign.callingHoursEnd,
      }
    : undefined

  // --- Save flow -----------------------------------------------------------

  /**
   * Called by `CampaignGeneralTab` (via `formRef`-driven submit) with the
   * Zod-validated General values. Assembles the full payload and performs the
   * create/update API call.
   */
  async function handleGeneralSubmit(values: CampaignGeneralValues): Promise<void> {
    setSaving(true)
    try {
      // The backend schema accepts calling hours as optional strings (not
      // null); omit them when empty rather than sending `null`.
      const body: Record<string, unknown> = {
        name: values.name,
        // ITEM 57 — the API field is `dialerType`, not `mode`
        // (routes/campaigns/schemas.ts:47-49), and it rejects the body
        // otherwise. Combined with item 63's lowercase values, every campaign
        // create/update from this drawer failed validation: wrong field name
        // AND wrong case on the same field, so fixing either alone still 400s.
        dialerType: values.mode,
        maxCps: values.maxCps,
        maxAttemptsPerDay: values.maxAttemptsPerDay,
        config,
      }
      if (values.callingHoursStart !== null) {
        body.callingHoursStart = values.callingHoursStart
      }
      if (values.callingHoursEnd !== null) {
        body.callingHoursEnd = values.callingHoursEnd
      }

      if (campaign) {
        await api.put(`/campaigns/${campaign.id}`, body)
        toast.success('Campaign updated')
      } else {
        await api.post('/campaigns', body)
        toast.success('Campaign created')
      }
      onSaved()
    } catch (err) {
      // Keep the drawer open on failure; surface a readable error.
      toast.error(err instanceof Error ? err.message : 'Failed to save campaign.')
    } finally {
      setSaving(false)
    }
  }

  // --- Render --------------------------------------------------------------

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      onSave={async () => {}}
      // When the Config JSON is invalid, drop `formRef` so the Save button
      // cannot drive a submit; `disabled` also disables it (see below).
      {...(configValid ? { formRef } : {})}
      // `saving` is the real save-in-progress state (drives the spinner);
      // `disabled` disables Save without a spinner when Config JSON is invalid.
      saving={saving}
      disabled={!configValid}
      width="wide"
      title={campaign ? `Edit Campaign: ${campaign.name}` : 'New Campaign'}
    >
      <div key={contentKey}>
        <TabView>
          <TabPanel header="General">
            <CampaignGeneralTab
              {...(generalDefaults ? { defaultValues: generalDefaults } : {})}
              onSubmit={handleGeneralSubmit}
              formRef={formRef}
            />
          </TabPanel>

          <TabPanel header="Contacts">
            {isEdit ? (
              <CampaignContactsTab
                campaignId={campaign.id}
                onImported={onSaved}
              />
            ) : (
              <p className="py-4 text-sm text-gray-500">
                Save campaign first to import contacts.
              </p>
            )}
          </TabPanel>

          <TabPanel header="Config">
            <CampaignConfigTab
              value={config}
              onChange={setConfig}
              onValidityChange={setConfigValid}
            />
          </TabPanel>

          <TabPanel header="Campaign Management">
            <WebContactManagementTab disabled={saving} />
          </TabPanel>
        </TabView>
      </div>
    </ResourceDrawer>
  )
}
